import {
  CSSProperties,
  DetailedHTMLProps,
  FC,
  Fragment,
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
  TableHTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from 'react'
import classNames from 'classnames'

import { useHorizontalScroll } from '../../../hooks/useHorizontalScroll'
import { TMGrayLogo } from '../../../icons/Logos/TMGrayLogo'
import { NavigationDownSmallIcon } from '../../../icons/NavigationDownSmallIcon'
import { NavigationUpSmallIcon } from '../../../icons/NavigationUpSmallIcon'
import { TableEmptyState } from '../../../icons/TableEmptyState'
import { SortOrder } from '../../../utils/ApiClient'
import { AlertIconWrapper } from '../../AlertIconWrapper/AlertIconWrapper'
import { Tooltip } from '../../Popups/Tooltip/Tooltip'
import { Truncate } from '../../Truncate/Truncate'
import { TextLargeStrong } from '../../Typography/Typography'
import { CheckBox } from '../CheckBox/CheckBox'
import { Scrollbar } from '../Scrollbar/Scrollbar'

import styles from './Table.module.scss'
import { DropArrowUpIcon } from '../../../icons/DropArrowUpIcon'

export type Sticky = 'left'

export type TableProps = DetailedHTMLProps<
  TableHTMLAttributes<HTMLTableElement>,
  HTMLTableElement
> & {
  wrapperClassName?: string
  sticky?: Sticky
}

export const Table: FC<PropsWithChildren<TableProps>> = (props) => {
  const { children, className, wrapperClassName, sticky, ...restProps } = props

  const {
    scrollContainerRef,
    scrollContainerInnerRef,
    hasScrolledToEnd,
    hasScrolledToStart,
    isScrollable,
  } = useHorizontalScroll<HTMLTableElement>()

  return (
    <div
      className={classNames(styles.tableWrapper, wrapperClassName, {
        [styles.containerScrollable]: isScrollable,
        [styles.scrollContainerFadeEnd]: isScrollable && !hasScrolledToEnd,
        [styles.stickyLeft]: sticky === 'left' && !hasScrolledToStart,
      })}
    >
      <div className={styles.scrollContainer} ref={scrollContainerRef}>
        <table
          ref={scrollContainerInnerRef}
          className={classNames(styles.table, className)}
          {...restProps}
        >
          {children}
        </table>
      </div>
      <Scrollbar parentRef={scrollContainerRef} />
    </div>
  )
}

type TableHeaderProps = DetailedHTMLProps<
  HTMLAttributes<HTMLTableSectionElement>,
  HTMLTableSectionElement
>

export const TableHeader: FC<PropsWithChildren<TableHeaderProps>> = (props) => {
  const { children, className, ...restProps } = props

  return (
    <thead {...restProps} className={classNames(styles.tableHeader, className)}>
      {children}
    </thead>
  )
}

type TableBodyProps = DetailedHTMLProps<
  HTMLAttributes<HTMLTableSectionElement>,
  HTMLTableSectionElement
>

export const TableBody: FC<PropsWithChildren<TableBodyProps>> = (props) => {
  const { children, className, ...restProps } = props

  return (
    <tbody className={classNames(styles.tableBody, className)} {...restProps}>
      {children}
    </tbody>
  )
}

interface ExpandableRowProps<RowType, SubType = RowType> {
  row: RowType & { id: string; subRows?: SubType[] }
  isExpanded: boolean
  onToggle: () => void
  renderParent: (row: RowType) => ReactNode[]
  renderSubRow: (sub: SubType) => ReactNode[]
}

export const ExpandableRow = <RowType, SubType extends { id: string } = RowType & { id: string }>({
  row,
  isExpanded,
  onToggle,
  renderParent,
  renderSubRow,
}: ExpandableRowProps<RowType, SubType>): JSX.Element => {
  const handleToggle = () => {
    if (row.subRows?.length) {
      onToggle()
    }
  }

  const parentCells = renderParent(row)

  return (
    <Fragment key={row.id}>
      <TableRow
        className={classNames(
          styles.expandableParentRow,
          {
            [styles.expandedParentRow]: isExpanded,
          },
          {
            [styles.expandableParentRowWithChildren]: row.subRows?.length,
          }
        )}
      >
        <TableCell onClick={handleToggle} className={styles.expandToggleCell}>
          <div className={styles.expandToggleCellInnerWrapper}>
            {parentCells[0]}
            {row.subRows?.length ? (
              <DropArrowUpIcon
                className={classNames(styles.expandToggleIcon, {
                  [styles.expandToggleIconRotated]: isExpanded,
                })}
              />
            ) : null}
          </div>
        </TableCell>

        {parentCells.slice(1).map((cell, i) => (
          <TableCell key={i}>{cell}</TableCell>
        ))}
      </TableRow>

      {isExpanded &&
        row.subRows?.map((sub) => {
          const subCells = renderSubRow(sub)
          return (
            <TableRow className={styles.expandedRow} key={sub.id}>
              {subCells.map((cell, i) => (
                <TableCell key={i} className={classNames({ [styles.expandedChildCell]: i === 0 })}>
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          )
        })}
    </Fragment>
  )
}

type TableRowProps = DetailedHTMLProps<HTMLAttributes<HTMLTableRowElement>, HTMLTableRowElement> & {
  selected?: boolean
}

export const TableRow: FC<PropsWithChildren<TableRowProps>> = (props) => {
  const { children, className, selected, ...restProps } = props

  return (
    <tr
      className={classNames(styles.tableRow, className, {
        [styles.selected]: selected,
      })}
      {...restProps}
    >
      {children}
    </tr>
  )
}

type TableHeadProps = DetailedHTMLProps<
  ThHTMLAttributes<HTMLTableCellElement>,
  HTMLTableCellElement
>

export const TableHead: FC<PropsWithChildren<TableHeadProps>> = (props) => {
  const { children, className, ...restProps } = props

  return (
    <th className={classNames(styles.tableHead, className)} {...restProps}>
      {children}
    </th>
  )
}

interface Props extends TableHeadProps {
  id?: string
  className?: string
  style?: CSSProperties
  align?: SortHeaderAlignType
  sort?: string | { sort: string; sortOrder: SortOrder }[] | null
  sortOrder?: SortOrder
  startWithOrder?: SortOrder

  setSort?(id: string, order: SortOrder): void | undefined
}

export type SortHeaderAlignType = 'center' | 'left' | 'right' | 'justify' | 'char'

export const TableHeadSort: FC<PropsWithChildren<Props>> = (props) => {
  const { id, sort, sortOrder, startWithOrder, style, className, setSort, ...rest } = props

  const currentSort = Array.isArray(sort)
    ? sort.find((s) => s.sort === id)?.sortOrder
    : sort === id
    ? sortOrder
    : null

  const selected = !!currentSort
  const isASC = currentSort === 'ASC'
  const isDESC = currentSort === 'DESC'

  let nextSort: SortOrder = isASC ? 'DESC' : 'ASC'

  if (id !== sort) {
    nextSort = startWithOrder || 'ASC'
  }

  return (
    <TableHead
      style={style}
      className={classNames(className)}
      onClick={(event) => {
        const target = event.target as HTMLElement

        const isTarget = target.closest('.tableHeadSort')
        isTarget && id && setSort?.(id, nextSort)
      }}
      {...rest}
    >
      <span
        className={classNames(styles.tableHeadSort, 'tableHeadSort', {
          [styles.alignLeft]: props.align === 'left',
          [styles.alignCenter]: props.align === 'center',
          [styles.alignRight]: props.align === 'right',
        })}
      >
        {props.children}
        {selected && isASC && <NavigationUpSmallIcon className={styles.sortIcon} />}
        {selected && isDESC && <NavigationDownSmallIcon className={styles.sortIcon} />}
      </span>
    </TableHead>
  )
}

type TableCellProps = DetailedHTMLProps<
  TdHTMLAttributes<HTMLTableCellElement>,
  HTMLTableCellElement
> & {
  checkboxTooltipText?: string
  onSelect?(): void
  disabled?: boolean
  selected?: boolean
  alert?: boolean
}

export const TableCell: FC<PropsWithChildren<TableCellProps>> = (props) => {
  const { children, className, onSelect, disabled, selected, checkboxTooltipText, alert, ...rest } =
    props

  return (
    <td {...rest} className={classNames(styles.tableCell, className)}>
      <AlertIconWrapper
        className={classNames(styles.cellAlert, {
          [styles.alignLeft]: rest.align === 'left',
          [styles.alignCenter]: rest.align === 'center',
          [styles.alignRight]: rest.align === 'right',
        })}
        asElement
        gapped
        isActive={alert}
      >
        <div className={styles.cellInnerWrapper}>
          {onSelect && (
            <div className={styles.checkboxWrapper}>
              <Tooltip.New direction='topRight' content={checkboxTooltipText}>
                <CheckBox disabled={disabled} value={!!selected} onClick={onSelect} />
              </Tooltip.New>
            </div>
          )}
          <Truncate>{children}</Truncate>
        </div>
      </AlertIconWrapper>
    </td>
  )
}

interface TableNoResultsProps {
  title: string
  content?: ReactNode
}

export const TableNoResults: FC<TableNoResultsProps> = (props) => {
  const { title, content } = props

  return (
    <div className={styles.tableNoResults}>
      <TableEmptyState />
      <div className={styles.infoWrapper}>
        <TMGrayLogo />
        <TextLargeStrong>{title}</TextLargeStrong>
        <div>{content}</div>
      </div>
    </div>
  )
}
